import { useState, useRef, useMemo } from "react";
import { S3_BASE_URL } from "src/constants/endpoints";
import RestuarantImg from "src/assests/images/dashboard/default-thumbnail (1).jpg";
import useOutsideClick from "src/hooks/useOutsideClick";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import { resolveLocalizedDisplayName } from "src/utils/localeTranslations";
import { BusinessDataTranslation } from "src/interfaces/menu.interface";

interface ComboCardProps {
  combo: {
    correlationId: string;
    englishName?: string;
    nonEnglishName?: string;
    bucketKeyName?: string;
    priceCents?: number;
    active?: boolean;
    translations?: BusinessDataTranslation[];
    itemList?: Array<{ englishName?: string }>;
  };
  restaurantCorrelationId: string;
  onEdit: (combo: ComboCardProps["combo"]) => void;
  onDuplicate?: (combo: ComboCardProps["combo"]) => void;
  onDelete: (comboId: string) => void;
  onCardClick: (combo: ComboCardProps["combo"]) => void;
}

export default function ComboCard({
  combo,
  restaurantCorrelationId,
  onEdit,
  onDuplicate,
  onDelete,
  onCardClick,
}: ComboCardProps) {
  const { enabledLocales, defaultLocale } = useStorePosLocales();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref: overflowRef, handler: () => setOverflowOpen(false) });

  const imageSrc = combo?.bucketKeyName
    ? combo.bucketKeyName.includes("blob")
      ? combo.bucketKeyName
      : S3_BASE_URL + combo.bucketKeyName
    : RestuarantImg;

  const itemCount = combo?.itemList?.length ?? 0;
  const itemNames = combo?.itemList?.slice(0, 2).map((i) => i?.englishName?.trim()).filter(Boolean) ?? [];
  const remainingCount = Math.max(0, itemCount - 2);
  const title = useMemo(
    () =>
      resolveLocalizedDisplayName(combo?.translations, enabledLocales, defaultLocale, {
        englishName: combo?.englishName,
        nonEnglishName: combo?.nonEnglishName,
      }) || "—",
    [combo?.translations, combo?.englishName, combo?.nonEnglishName, enabledLocales, defaultLocale]
  );
  const isInactive = combo.active === false;

  const handleCardClick = () => onCardClick(combo);

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOverflowOpen(false);
    onEdit(combo);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOverflowOpen(false);
    onDuplicate?.(combo);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOverflowOpen(false);
    onDelete(combo.correlationId);
  };

  return (
    <div
      className="w-full cursor-pointer group"
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
      role="button"
      tabIndex={0}
      aria-label={`Open ${title}, ${itemCount} items`}
    >
      <div
        className={`
          relative w-full flex flex-col
          xl:w-[240px] w-[220px] max-w-full
          border border-black/[0.05] bg-[#FFFFFF] rounded-xl
          shadow-[0_4px_12px_rgba(0,0,0,0.04)]
          transition-all duration-[200ms] ease-out
          hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-black/[0.08] hover:-translate-y-[2px]
          active:scale-[0.99]
          ${isInactive ? "opacity-[0.88]" : ""}
        `}
      >
        {/* 3-dot menu — same as Categories / Items */}
        <div
          className="absolute right-2 top-2 z-10"
          ref={overflowRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="min-h-[44px] min-w-[44px] rounded-lg flex items-center justify-center text-[#6b7280] opacity-[0.85] hover:opacity-100 hover:bg-black/[0.08] transition-all duration-150 border-0"
            aria-label="Combo actions"
            onClick={(e) => {
              e.stopPropagation();
              setOverflowOpen((o) => !o);
            }}
          >
            <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <circle cx="10" cy="4" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="10" cy="16" r="1.5" />
            </svg>
          </button>
          {overflowOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 min-w-[120px] py-1 bg-white rounded-lg border border-[#E5E7EB] shadow-lg"
              role="menu"
            >
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                role="menuitem"
                onClick={handleEdit}
              >
                Edit Combo
              </button>
              {onDuplicate && (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  role="menuitem"
                  onClick={handleDuplicate}
                >
                  Duplicate
                </button>
              )}
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                role="menuitem"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          )}
        </div>

        {/* Image — consistent height, center crop to avoid awkward zoom */}
        <div className="relative flex justify-center bg-[#EEF0F2] h-[99px] rounded-t-xl overflow-hidden shrink-0 border-b border-black/[0.05]">
          <img
            alt=""
            className={`w-full h-full object-cover object-center transition-[filter] duration-200 group-hover:brightness-[1.02] ${isInactive ? "grayscale-[0.35]" : ""}`}
            src={imageSrc}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.03))" }}
            aria-hidden
          />
        </div>

        {/* Content — more internal padding, refined hierarchy */}
        <div className="px-4 pt-5 pb-3">
          <h2 className="text-sm font-semibold text-[#111] truncate leading-tight" style={{ fontWeight: 600 }}>
            {title}
          </h2>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {isInactive ? (
              <span className="inline-flex items-center w-fit text-[11px] text-[#92400E] font-medium rounded-full border border-[#FCD34D] bg-[#FFFBEB] py-0.5 px-2">
                Inactive
              </span>
            ) : null}
            <span className="inline-flex items-center text-[11px] text-[#6B7280] font-normal rounded-full border border-[#E8EAED] bg-[#F9FAFB] py-0.5 px-2">
              {itemCount === 1 ? "1 item" : `${itemCount} items`}
            </span>
          </div>
          {itemNames.length > 0 && (
            <p className="text-[11px] text-[#6B7280]/60 mt-3 line-clamp-2 leading-tight">
              {itemNames.join(" · ")}
              {remainingCount > 0 && ` · +${remainingCount} more`}
            </p>
          )}
          {combo?.priceCents != null && combo.priceCents > 0 && (
            <p className="text-[15px] font-semibold text-[#111] mt-3 tabular-nums" style={{ fontWeight: 600 }}>
              {formatCentsToDollarsWithDollarSign(combo.priceCents)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
